/**
 * RewardsList - Displays earned rewards with status badges.
 * Shows reward amounts, descriptions, and application status.
 */

import type { ReferralReward } from "../../hooks/useReferrals";

export interface RewardsListProps {
  rewards: ReferralReward[];
}

export function RewardsList({ rewards }: RewardsListProps) {
  if (rewards.length === 0) {
    return (
      <div className="rounded-2xl border-[3px] border-dashed border-neutral-200 bg-neutral-50 p-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sunshine-100 to-sunshine-200 rounded-full flex items-center justify-center">
            <GiftIcon className="w-8 h-8 text-sunshine-500" />
          </div>
          <div>
            <p className="font-display font-bold text-body-lg text-neutral-700">
              No rewards yet
            </p>
            <p className="text-body-sm text-neutral-500 mt-1">
              When friends subscribe, your rewards will appear here!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group rewards by status
  const pendingRewards = rewards.filter((r) => r.status === "pending");
  const appliedRewards = rewards.filter((r) => r.status === "applied");
  const expiredRewards = rewards.filter(
    (r) => r.status === "expired" || r.status === "voided"
  );

  return (
    <div className="space-y-4">
      {/* Pending rewards - highlight these */}
      {pendingRewards.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-sunshine-500 rounded-full animate-pulse" />
            <p className="font-display font-bold text-body-sm text-neutral-600 uppercase tracking-wider">
              Pending Rewards
            </p>
          </div>
          <div className="space-y-2">
            {pendingRewards.map((reward) => (
              <RewardCard key={reward.id} reward={reward} />
            ))}
          </div>
        </div>
      )}

      {/* Applied rewards */}
      {appliedRewards.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-meadow-500 rounded-full" />
            <p className="font-display font-bold text-body-sm text-neutral-600 uppercase tracking-wider">
              Applied Rewards
            </p>
          </div>
          <div className="space-y-2">
            {appliedRewards.map((reward) => (
              <RewardCard key={reward.id} reward={reward} />
            ))}
          </div>
        </div>
      )}

      {/* Expired rewards - muted styling */}
      {expiredRewards.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-neutral-400 rounded-full" />
            <p className="font-display font-bold text-body-sm text-neutral-500 uppercase tracking-wider">
              Expired
            </p>
          </div>
          <div className="space-y-2 opacity-60">
            {expiredRewards.map((reward) => (
              <RewardCard key={reward.id} reward={reward} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RewardCard({ reward }: { reward: ReferralReward }) {
  const getStatusStyles = () => {
    switch (reward.status) {
      case "pending":
        return {
          bg: "bg-gradient-to-br from-sunshine-50 to-sunshine-100",
          border: "border-sunshine-300",
          badge: "bg-sunshine-100 border-sunshine-400 text-sunshine-700",
          badgeIcon: ClockIcon,
          badgeText: "Pending",
        };
      case "applied":
        return {
          bg: "bg-gradient-to-br from-meadow-50 to-meadow-100",
          border: "border-meadow-300",
          badge: "bg-meadow-100 border-meadow-400 text-meadow-700",
          badgeIcon: CheckIcon,
          badgeText: "Applied",
        };
      case "expired":
      case "voided":
      default:
        return {
          bg: "bg-neutral-50",
          border: "border-neutral-200",
          badge: "bg-neutral-100 border-neutral-300 text-neutral-500",
          badgeIcon: XIcon,
          badgeText: reward.status,
        };
    }
  };

  const styles = getStatusStyles();
  const BadgeIcon = styles.badgeIcon;

  return (
    <div
      className={`
        flex items-center justify-between
        p-4 rounded-xl
        border-[3px] ${styles.border}
        ${styles.bg}
        transition-all duration-200
        hover:shadow-md
      `}
    >
      {/* Left side: Gift icon + Details */}
      <div className="flex items-center gap-4">
        <div
          className={`
            w-12 h-12 rounded-full
            bg-white/80
            flex items-center justify-center
            shadow-sm
          `}
        >
          <GiftIcon
            className={`
              w-6 h-6
              ${reward.status === "pending" ? "text-sunshine-500" : ""}
              ${reward.status === "applied" ? "text-meadow-500" : ""}
              ${reward.status === "expired" || reward.status === "voided" ? "text-neutral-400" : ""}
            `}
          />
        </div>

        <div>
          <p className="font-display font-bold text-body-md text-neutral-800">
            ${(reward.rewardValueCents / 100).toFixed(0)} Free Month
          </p>
          <p className="text-body-sm text-neutral-500">
            {reward.appliedAt
              ? `Applied ${formatDate(reward.appliedAt)}`
              : reward.expiresAt
                ? `Expires ${formatDate(reward.expiresAt)}`
                : reward.rewardDescription}
          </p>
        </div>
      </div>

      {/* Right side: Status badge */}
      <div
        className={`
          flex items-center gap-1.5 px-3 py-1.5
          rounded-full border-2
          ${styles.badge}
        `}
      >
        <BadgeIcon className="w-4 h-4" />
        <span className="font-bold text-body-sm capitalize">{styles.badgeText}</span>
      </div>
    </div>
  );
}

// Helper function to format dates
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Icons
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default RewardsList;
